import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FieldHeader, FieldTitleRow } from '../../components/field/FieldPageChrome';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstantDay, tenantHour } from '../../lib/calendar-date';

// The hour and the day are the TENANT's (companies.time_zone), not the
// phone's: a hand whose phone is on another zone still works the yard's day.
const greetingFor = (d: Date, timeZone: string | null) => {
  const h = tenantHour(timeZone, d);
  // No zone, no hour: greet without claiming a time of day.
  if (h === null) return 'Hello';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const dateLabel = (d: Date, timeZone: string | null) =>
  formatInstantDay(d.toISOString(), timeZone, { weekday: 'long', month: 'short', day: 'numeric' }, 'en-US') ?? '—';

export default function MyWorkLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const now = new Date();

  /*
   * GET /auth/me sends `fullName` and no first/last split, so the header read
   * "??" and greeted nobody by name for every field login (Cody Rempel on Apex
   * Hauling, fix/driver-app-trucking). The split spellings are read first for
   * a session restored from an older payload. No name at all greets without
   * one, never "??".
   */
  const u = user as { first_name?: string | null; last_name?: string | null; fullName?: string | null; full_name?: string | null; name?: string | null } | null | undefined;
  const nameParts = ([u?.first_name, u?.last_name].filter(Boolean).join(' ') || u?.fullName || u?.full_name || u?.name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const firstName = nameParts[0] ?? null;

  /*
   * ONE HOME, NO BOTTOM BAR (fix/field-home-trucking-rentals). /my-work itself
   * is the home (FieldHomePage: the clock card and the tiles). Every other page
   * in this shell was a tab on a bottom bar; it now has a back arrow to the
   * home, on the title line, and the greeting stays on the home. The clock is
   * the home's card, so the header no longer repeats a Clock In button.
   */
  const { pathname } = useLocation();
  const isHome = pathname === '/my-work' || pathname === '/my-work/';
  const PAGE_TITLE: Record<string, string> = {
    '/my-work/today': 'Today',
    '/my-work/schedule': 'Schedule',
    '/my-work/forms': 'Forms',
    '/my-work/history': 'History',
    '/my-work/profile': 'Profile',
  };
  const pageTitle = PAGE_TITLE[pathname.replace(/\/$/, '')] ?? '';

  return (
    <div className="min-h-full bg-fieldiq-dark flex flex-col">
      {/* The casing field header, the shared component (fe#815 follow-up to
          #1054): logo + company, search, AI, bell, settings. The greeting
          stays on the home; every other page has a back arrow to it. */}
      <div className="max-w-[1920px] w-full mx-auto">
        <FieldHeader />
        {isHome ? (
          <div className="px-4 pt-4">
            <p className="text-[11px] uppercase tracking-wider text-fieldiq-text">
              {dateLabel(now, timeZone)}
            </p>
            <h1 className="mt-0.5 text-2xl font-bold leading-tight">
              {greetingFor(now, timeZone)}{firstName ? `, ${firstName}` : ''}
            </h1>
          </div>
        ) : (
          <FieldTitleRow
            title={pageTitle}
            onBack={() => navigate('/my-work')}
            backLabel="Back to home"
            backTestId="field-back-home"
          />
        )}
      </div>

      {/* Page content */}
      <main className="flex-1 pb-8">
        <div className="max-w-[1920px] mx-auto px-4 py-4">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
